import { RRule } from "rrule";
import type { Event } from "@lemmonhq/types";

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Returns the next relevant occurrence for an event at/after `now`, or null if
 * the event has no upcoming occurrence.
 *
 * Recurring events store `schedule.start_at` at their FIRST occurrence, which is
 * usually in the past. Comparing that against `now` would incorrectly hide a
 * still-active recurring event, so we expand the rrule to find the next future
 * occurrence (respecting `end_at` and `excluded_dates`).
 */
export const getNextEventOccurrence = (
  event: Pick<Event, "date" | "schedule">,
  now: Date = new Date(),
): Date | null => {
  const schedule = event.schedule;

  if (schedule?.type === "recurring") {
    try {
      const rrule = RRule.fromString(schedule.rrule);
      const dtstart = new Date(schedule.start_at);
      const rule = new RRule({ ...rrule.origOptions, dtstart });
      const excludedDates = new Set(
        (schedule.excluded_dates || []).map((d) => formatDateLocal(new Date(d))),
      );

      // Prefer the series end encoded in the rrule (UNTIL/COUNT). If the rrule
      // is unbounded, fall back to the event's `end_at` so an open-ended rule
      // doesn't recur forever.
      const ruleIsBounded =
        rule.origOptions.until != null || rule.origOptions.count != null;
      const seriesEnd =
        !ruleIsBounded && schedule.end_at ? new Date(schedule.end_at) : null;

      // Walk forward through occurrences, skipping any excluded dates.
      let cursor: Date | null = rule.after(now, true);
      while (cursor) {
        if (seriesEnd && cursor > seriesEnd) return null;
        if (!excludedDates.has(formatDateLocal(cursor))) return cursor;
        cursor = rule.after(cursor, false);
      }
      return null;
    } catch (e) {
      console.error("Error parsing RRule for event:", e);
      return null;
    }
  }

  if (schedule?.type === "one_off") {
    const startAt = new Date(schedule.start_at);
    return startAt >= now ? startAt : null;
  }

  // No structured schedule: fall back to the plain `date` field, treating the
  // whole day as upcoming until it ends.
  const eventDate = new Date(event.date);
  if (Number.isNaN(eventDate.getTime())) return null;
  eventDate.setHours(23, 59, 59, 999);
  return eventDate >= now ? eventDate : null;
};
